import React, { useEffect, useContext, useState } from 'react';

import appConfigs from '../../config';

import { Tooltip } from '@mui/material';

import { GeneralContext } from '../../core/app-context/GeneralContext';
import { useFileUploader } from '../../shared/hooks/use-file-uploader';
import { ColorPickers } from './components/ColorPickers';
import { ConfigCard } from '../../shared/components/ConfigCard';
import { CopyableUrl } from './components/CopyableUrl';
import { FileDropzone } from './components/FileDropzone';
import { FontSelector } from './components/FontSelector';
import { HeaderInfo } from './components/HeaderInfo';
import { PageLinksInputs } from './components/PageLinksInputs';
import { SocialMediaInputs } from './components/SocialMediaInputs';
import { CustomDeleteIcon } from './components/CustomDeleteIcon';
import { MainImageSizingSelector } from './components/MainImageSizingSelector';
import { useGetDevolutionPageConfigs } from './hooks/use-get-devolution-page-configs';
import { usePageConfigsState } from './hooks/use-page-configs-state';
import { useUpsertDevolutionPageConfigs } from './hooks/use-upsert-devolution-page-configs';
import {
  CardsDivider,
  InsideCardDivider,
} from './styles';

import { PrivatePageStructure } from '../../shared/components/PrivatePageStructure';

export default function DevolutionPageConfig() {
  const { setSideMenu, setIdMenuSelected, store } = useContext(GeneralContext);
  const storeId = store?.id;
  const {
    configs: fetchedData,
    getDevolutionPageConfigs,
    loading,
  } = useGetDevolutionPageConfigs({ storeId });
  const { upsertDevolutionPageConfigs } = useUpsertDevolutionPageConfigs();
  const { uploadFile, deleteFile } = useFileUploader();
  const {
    configs,
    setConfigs,
    logoFile,
    setLogoFile,
    faviconFile,
    setFaviconFile,
    mainImageFile,
    setMainImageFile,
    mainImageMobileFile,
    setMainImageMobileFile,
    discartChanges,
    isChanged,
  } = usePageConfigsState({ initialData: fetchedData });
  const [openEditEndpointTexfield, setOpenEditEndpointTexfield] =
    useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setSideMenu('reversa');
    setIdMenuSelected('pageDevolution');
  }, [setSideMenu, setIdMenuSelected]);

  const handleFilesUpsert = async () => {
    if (!configs) return;

    let { logoUrl, faviconUrl, mainImageUrl, mainImageMobileUrl } = configs;

    if (logoFile) {
      if (configs.logoUrl) {
        await deleteFile(storeId, configs.logoUrl);
      }
      logoUrl = await uploadFile(logoFile);
      if (logoUrl) {
        setConfigs((prev) => ({ ...prev, logoUrl }));
        setLogoFile(null);
      }
    }
    if (faviconFile) {
      if (configs.faviconUrl) {
        await deleteFile(storeId, configs.faviconUrl);
      }
      faviconUrl = await uploadFile(faviconFile);
      if (faviconUrl) {
        setConfigs((prev) => ({ ...prev, faviconUrl }));
        setFaviconFile(null);
      }
    }
    if (mainImageFile) {
      if (configs.mainImageUrl) {
        await deleteFile(storeId, configs.mainImageUrl);
      }
      mainImageUrl = await uploadFile(mainImageFile);
      if (mainImageUrl) {
        setConfigs((prev) => ({ ...prev, mainImageUrl }));
        setMainImageFile(null);
      }
    }
    if (mainImageMobileFile) {
      if (configs.mainImageMobileUrl) {
        await deleteFile(storeId, configs.mainImageMobileUrl);
      }
      mainImageMobileUrl = await uploadFile(mainImageMobileFile);
      if (mainImageMobileUrl) {
        setConfigs((prev) => ({ ...prev, mainImageMobileUrl }));
        setMainImageMobileFile(null);
      }
    }
    return { logoUrl, faviconUrl, mainImageUrl, mainImageMobileUrl };
  };

  const handleSubmit = async () => {
    if (!configs) return;
    setSaving(true);
    const { logoUrl, faviconUrl, mainImageUrl, mainImageMobileUrl } =
      await handleFilesUpsert();
    await upsertDevolutionPageConfigs({
      storeId,
      configs: {
        ...configs,
        logoUrl,
        faviconUrl,
        mainImageUrl,
        mainImageMobileUrl,
      },
    });
    await getDevolutionPageConfigs();
    setOpenEditEndpointTexfield(false);
    setSaving(false);
  };

  const handleDiscartChanges = () => {
    discartChanges();
    setOpenEditEndpointTexfield(false);
  };

  const getDevolutionPageUrl = () => {
    return `${appConfigs.frontendUrl}/c/${configs?.publicPageEndpoint}`;
  };

  const handleDeleteImage = async (url) => {
    if (url) {
      await deleteFile(storeId, url);
      await getDevolutionPageConfigs();
    }
  };

  const toLowerSnakeCase = (str) => {
    return str
      .replace(/\W+/g, ' ')
      .split(/ |\B(?=[A-Z])/)
      .map((word) => word.toLowerCase())
      .join('_');
  };

  return (
    <PrivatePageStructure
      title={<HeaderInfo pageLink={getDevolutionPageUrl()} />}
      loading={loading}
      btn1Action={handleDiscartChanges}
      btn2Action={handleSubmit}
      btn2Loading={saving}
    >
      <ConfigCard justifyContent="space-between" title="URL da página">
        <CopyableUrl
          url={getDevolutionPageUrl()}
          endpoint={configs?.publicPageEndpoint}
          setEndpoint={(newValue) => {
            setConfigs((prev) => ({
              ...prev,
              publicPageEndpoint: toLowerSnakeCase(newValue),
            }));
          }}
          openTexfield={openEditEndpointTexfield}
          setOpenTexfield={setOpenEditEndpointTexfield}
        />
      </ConfigCard>
      <CardsDivider />
      <ConfigCard
        justifyContent="flex-start"
        title="Fontes"
        subtitle="Selecione a fonte de preferência"
      >
        <FontSelector
          fontFamily={configs?.fontFamily}
          setFontFamily={(newValue) => {
            setConfigs((prev) => ({ ...prev, fontFamily: newValue }));
          }}
        />
      </ConfigCard>
      <CardsDivider />
      <ConfigCard
        justifyContent="flex-start"
        title="Cores"
        subtitle="Selecione as cores da página"
      >
        <ColorPickers
          backgroundColor={configs?.backgroundColor || '#000000'}
          setBackgroundColor={(newValue) => {
            setConfigs((prev) => ({ ...prev, backgroundColor: newValue }));
          }}
          headerFooterColor={configs?.headerFooterColor || '#ffffff'}
          setHeaderFooterColor={(newValue) => {
            setConfigs((prev) => ({ ...prev, headerFooterColor: newValue }));
          }}
          primaryColor={configs?.primaryColor || '#000000'}
          setPrimaryColor={(newValue) => {
            setConfigs((prev) => ({ ...prev, primaryColor: newValue }));
          }}
          secondaryColor={configs?.secondaryColor || '#000000'}
          setSecondaryColor={(newValue) => {
            setConfigs((prev) => ({ ...prev, secondaryColor: newValue }));
          }}
          fontColor={configs?.fontColor || '#000000'}
          setFontColor={(newValue) => {
            setConfigs((prev) => ({ ...prev, fontColor: newValue }));
          }}
          fontColorSecondary={configs?.fontColorSecondary || '#ffffff'}
          setFontColorSecondary={(newValue) => {
            setConfigs((prev) => ({
              ...prev,
              fontColorSecondary: newValue,
            }));
          }}
          footerIconsColor={configs?.footerIconsColor || '#000000'}
          setFooterIconsColor={(newValue) => {
            setConfigs((prev) => ({
              ...prev,
              footerIconsColor: newValue,
            }));
          }}
        />
      </ConfigCard>
      <CardsDivider />
      <ConfigCard
        title="Logo"
        subtitle="PNG, JPG ou JPEG - 2MB máximo"
        trallingIcon={
          <Tooltip title="Deletar logo salvo" placement="left-start">
            <div>
              <CustomDeleteIcon
                onClick={async () => {
                  await handleDeleteImage(configs?.logoUrl);
                }}
                enable={!!configs?.logoUrl}
              />
            </div>
          </Tooltip>
        }
      >
        <FileDropzone
          fileUrl={configs?.logoUrl}
          file={logoFile}
          setFile={setLogoFile}
          maxSize={2000000}
          acceptedFilesTypes={{
            'image/png': ['.png'],
            'image/jpeg': ['.jpeg'],
            'image/jpg': ['.jpg'],
          }}
        />
      </ConfigCard>
      <CardsDivider />
      <ConfigCard
        title="Favicon"
        subtitle="PNG ou ICO - 250KB máximo"
        trallingIcon={
          <Tooltip title="Deletar favicon salvo" placement="left-start">
            <div>
              <CustomDeleteIcon
                onClick={async () => {
                  await handleDeleteImage(configs?.faviconUrl);
                }}
                enable={!!configs?.faviconUrl}
              />
            </div>
          </Tooltip>
        }
      >
        <FileDropzone
          fileUrl={configs?.faviconUrl}
          file={faviconFile}
          setFile={setFaviconFile}
          maxSize={250000}
          acceptedFilesTypes={{
            'image/png': ['.png'],
            'image/ico': ['.ico'],
          }}
        />
      </ConfigCard>
      <CardsDivider />
      <ConfigCard
        title="Imagem principal"
        subtitle="PNG, JPG ou JPEG - 2MB máximo"
        flexDirection="column"
        trallingIcon={
          <Tooltip title="Deletar imagem salva" placement="left-start">
            <div>
              <CustomDeleteIcon
                onClick={async () => {
                  await handleDeleteImage(configs?.mainImageUrl);
                }}
                enable={!!configs?.mainImageUrl}
              />
            </div>
          </Tooltip>
        }
      >
        <FileDropzone
          fileUrl={configs?.mainImageUrl}
          file={mainImageFile}
          setFile={setMainImageFile}
          maxSize={2000000}
          acceptedFilesTypes={{
            'image/png': ['.png'],
            'image/jpeg': ['.jpeg'],
            'image/jpg': ['.jpg'],
          }}
        />
        <InsideCardDivider />
        <MainImageSizingSelector
          value={configs?.mainImageSizingMode}
          onChange={(e) => {
            setConfigs((prev) => ({
              ...prev,
              mainImageSizingMode: e.target.value,
            }));
          }}
        />
      </ConfigCard>
      <CardsDivider />
      <ConfigCard
        title="Imagem principal (mobile)"
        subtitle="PNG, JPG ou JPEG - 2MB máximo"
        flexDirection="column"
        trallingIcon={
          <Tooltip title="Deletar imagem salva" placement="left-start">
            <div>
              <CustomDeleteIcon
                onClick={async () => {
                  await handleDeleteImage(configs?.mainImageMobileUrl);
                }}
                enable={!!configs?.mainImageMobileUrl}
              />
            </div>
          </Tooltip>
        }
      >
        <FileDropzone
          fileUrl={configs?.mainImageMobileUrl}
          file={mainImageMobileFile}
          setFile={setMainImageMobileFile}
          maxSize={2000000}
          acceptedFilesTypes={{
            'image/png': ['.png'],
            'image/jpeg': ['.jpeg'],
            'image/jpg': ['.jpg'],
          }}
        />
        <InsideCardDivider />
        <MainImageSizingSelector
          value={configs?.mainImageMobileSizingMode}
          onChange={(e) => {
            setConfigs((prev) => ({
              ...prev,
              mainImageMobileSizingMode: e.target.value,
            }));
          }}
        />
      </ConfigCard>
      <CardsDivider />
      <ConfigCard
        justifyContent="flex-start"
        title="Redes sociais"
        subtitle="Adicione os links das redes sociais que deseja exibir. Para omitir na página, apenas deixar em branco."
      >
        <SocialMediaInputs
          instagramAccount={configs?.instagramAccount}
          setInstagramAccount={(newValue) => {
            setConfigs((prev) => ({ ...prev, instagramAccount: newValue }));
          }}
          facebookAccount={configs?.facebookAccount}
          setFacebookAccount={(newValue) => {
            setConfigs((prev) => ({ ...prev, facebookAccount: newValue }));
          }}
          linkedinAccount={configs?.linkedinAccount}
          setLinkedinAccount={(newValue) => {
            setConfigs((prev) => ({ ...prev, linkedinAccount: newValue }));
          }}
          tiktokAccount={configs?.tiktokAccount}
          setTiktokAccount={(newValue) => {
            setConfigs((prev) => ({ ...prev, tiktokAccount: newValue }));
          }}
        />
      </ConfigCard>
      <CardsDivider />
      <ConfigCard
        justifyContent="flex-start"
        title="Links da página"
        subtitle="Adicione os links extras que deseja exibir. Para omitir na página, apenas deixar em branco."
      >
        <PageLinksInputs
          contactEmail={configs?.contactEmail}
          setContactEmail={(newValue) => {
            setConfigs((prev) => ({ ...prev, contactEmail: newValue }));
          }}
          link1Title={configs?.link1Title}
          setLink1Title={(newValue) => {
            setConfigs((prev) => ({ ...prev, link1Title: newValue }));
          }}
          link1Url={configs?.link1Url}
          setLink1Url={(newValue) => {
            setConfigs((prev) => ({ ...prev, link1Url: newValue }));
          }}
          link2Title={configs?.link2Title}
          setLink2Title={(newValue) => {
            setConfigs((prev) => ({ ...prev, link2Title: newValue }));
          }}
          link2Url={configs?.link2Url}
          setLink2Url={(newValue) => {
            setConfigs((prev) => ({ ...prev, link2Url: newValue }));
          }}
        />
      </ConfigCard>
    </PrivatePageStructure>
  );
}
